import React, { useCallback } from 'react';
import * as R from 'ramda';
import { useIntl } from '@atlassian/jira-intl';
import { Lozenge, Checkbox } from '@atlassian/jira-portfolio-3-common';
import type {
	AtlaskitOptionProps,
	OptionsType,
	OptionType,
} from '@atlassian/jira-portfolio-3-common/src/select/types';
import { NO_SPRINT_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/dependencies';
import {
	getSprintStateLozengeContent,
	getSprintStateLabel,
	getLozengeAppearance,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/sprints/utils';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import { CHECKBOX_STATES } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import { STATE_CATEGORY } from './types';

const { UNCHECKED, INDETERMINATE } = CHECKBOX_STATES;

const CustomizedOption = (props: AtlaskitOptionProps<OptionType, boolean>) => {
	const intl = useIntl();
	const { label, data, isSelected, isDisabled, innerRef } = props;

	const onChange = useCallback(() => {
		if (data.type === STATE_CATEGORY) {
			if (data.checkboxState === UNCHECKED) {
				const newSelectedSprint = [];
				const allSprintOptionsById = R.indexBy(
					R.prop<string>('value'),
					props.options.reduce(
						(allOptions: OptionsType, option: OptionType) =>
							allOptions.concat(option?.options || []),
						[],
					),
				);
				const currentSelectedSprint = R.indexBy(R.prop<string>('value'), props.getValue() || []);
				for (const id of data.sprintIds) {
					if (!isDefined(currentSelectedSprint[id])) {
						newSelectedSprint.push(allSprintOptionsById[id]);
					}
				}
				props.setValue(props.getValue().concat(newSelectedSprint), 'select-option');
			} else {
				const sprintOptions = props
					.getValue()
					.filter((option: OptionType) => option.state === data.sprintState);
				props.setValue(R.difference(props.getValue(), sprintOptions), 'select-option');
			}
		} else if (!isSelected) {
			props.setValue(props.getValue().concat(data), 'select-option');
		} else {
			props.setValue(
				props.getValue().filter((option) => option.value !== data.value),
				'select-option',
			);
		}
	}, [props, data, isSelected]);

	const lozengeContent = getSprintStateLozengeContent(intl);
	const checkboxContent = getSprintStateLabel(intl);
	let isIndeterminate = false;
	let isChecked = isSelected;
	let isDisabledForCheckbox = isDisabled;
	let labelForCheckbox: string = label;
	let lozenge;

	if (data.type === STATE_CATEGORY) {
		isIndeterminate = data.checkboxState === INDETERMINATE;
		isChecked = data.checkboxState !== UNCHECKED;
		isDisabledForCheckbox = !data.sprintIds.length;
		labelForCheckbox = checkboxContent(data.sprintState);
	} else if (data.type === NO_SPRINT_ID) {
		labelForCheckbox = intl.formatMessage(commonMessages.noSprintLabel);
	} else {
		lozenge = (
			<Lozenge appearance={getLozengeAppearance(data.state)}>{lozengeContent(data.state)}</Lozenge>
		);
	}

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div ref={innerRef} className={styles.checkboxWrapper}>
			<Checkbox
				isIndeterminate={isIndeterminate}
				isChecked={isChecked}
				isDisabled={isDisabledForCheckbox}
				onChange={onChange}
				label={
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					<div className={styles['sprint-title-container']}>
						{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
						<span className={styles['sprint-title']}>{labelForCheckbox}</span>
						{lozenge}
					</div>
				}
			/>
		</div>
	);
};

export default CustomizedOption;
